import { z } from "zod";
import { isBefore } from "date-fns";
import { GenderSchema } from "../user";
import {
  toDatabaseFormat,
  getCurrentTimeRoundedTo5Minutes,
  getCurrentTimeRoundedTo5MinutesPlusHour,
} from "@ac-expo/utils";

const commercialMeetingBaseSchema = z.object({
  date: z.string(),
  beginningHour: z.string(),
  endHour: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().optional(),
  gender: GenderSchema,
  personalComment: z.string().optional(),
  connectId: z.string().optional(),
  ticketId: z.string().optional(),
  ticketCount: z.number().optional(),
});

export const commercialMeetingSchema = commercialMeetingBaseSchema.superRefine(
  (data, ctx) => {
    if (isBefore(new Date(data.date + " " + data.beginningHour), new Date())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["date"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["beginningHour"],
      });
    }
    if (
      isBefore(
        new Date(data.date + " " + data.endHour),
        new Date(data.date + " " + data.beginningHour)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de fin ne peut être avant celle du début.",
        path: ["endHour"],
      });
    }
    if (!data.connectId && !data.email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Un ID ou un Email doit être renseigné.",
        path: ["email"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Un ID ou un Email doit être renseigné.",
        path: ["connectId"],
      });
    }
  }
);

export const commercialExhibitorMeetingSchema = z
  .object({
    date: z.string(),
    beginningHour: z.string(),
    endHour: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().optional(),
    gender: GenderSchema,
    personalComment: z.string().optional(),
    connectId: z.string().optional(),
    commercialId: z.string(),
  })
  .superRefine((data, ctx) => {
    if (isBefore(new Date(data.date + " " + data.beginningHour), new Date())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["date"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["beginningHour"],
      });
    }
    if (
      isBefore(
        new Date(data.date + " " + data.endHour),
        new Date(data.date + " " + data.beginningHour)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de fin ne peut être avant celle du début.",
        path: ["endHour"],
      });
    }
    if (!data.connectId && !data.email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Un ID ou un Email doit être renseigné.",
        path: ["email"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Un ID ou un Email doit être renseigné.",
        path: ["connectId"],
      });
    }
  });

export const commercialExhibitorMeetingUpdateSchema = z.object({
  commercialId: z.string(),
  personalComment: z.string().optional(),
});

export const commercialMeetingUpdateSchema = z
  .object({
    date: z.string(),
    beginningHour: z.string(),
    endHour: z.string(),
    personalComment: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (isBefore(new Date(data.date + " " + data.beginningHour), new Date())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["date"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["beginningHour"],
      });
    }
    if (
      isBefore(
        new Date(data.date + " " + data.endHour),
        new Date(data.date + " " + data.beginningHour)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de fin ne peut être avant celle du début.",
        path: ["endHour"],
      });
    }
  });

export const commercialExhibitorSingleUnavailabilitySchema = z
  .object({
    date: z.string(),
    beginningHour: z.string(),
    endHour: z.string(),
    isFullDay: z.boolean(),
  })
  .superRefine((data, ctx) => {
    if (
      !data.isFullDay &&
      isBefore(
        new Date(data.date + " " + data.endHour),
        new Date(data.date + " " + data.beginningHour)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de fin ne peut être avant celle du début.",
        path: ["endHour"],
      });
    }
  });

export const commercialExhibitorUnavailabilitySchema = z.object({
  unavailabilities: z
    .array(commercialExhibitorSingleUnavailabilitySchema)
    .superRefine((unavailabilities, ctx) => {
      const datesSorted = (
        unavailabilities.map((u) => ({
          from: new Date(u.date + " " + u.beginningHour),
          to: new Date(u.date + " " + u.endHour),
        })) as { from: Date; to: Date }[]
      ).sort((a, b) => (isBefore(a.from, b.from) ? -1 : 1));

      for (let i = 1; i < datesSorted.length; i++) {
        const prevEnd = datesSorted[i - 1].to;
        const currentStart = datesSorted[i].from;
        if (currentStart <= prevEnd) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Les plages de dates ne doivent pas se chevaucher",
            path: [i, "dates"],
          });
        }
      }
    }),
  commercialId: z.string(),
});

export const commercialClientCommentSchema = z.object({
  personalComment: z.string(),
});

export type CommercialMeetingForm = z.infer<typeof commercialMeetingSchema>;
export type CommercialExhibitorMeetingForm = z.infer<
  typeof commercialExhibitorMeetingSchema
>;
export type CommercialMeetingUpdateForm = z.infer<
  typeof commercialMeetingUpdateSchema
>;
export type CommercialExhibitorMeetingUpdateForm = z.infer<
  typeof commercialExhibitorMeetingUpdateSchema
>;
export type CommercialClientCommentForm = z.infer<
  typeof commercialClientCommentSchema
>;
export type CommercialExhibitorUnavailabilityForm = z.infer<
  typeof commercialExhibitorUnavailabilitySchema
>;

export const defaultCommercialMeetingValues = {
  date: toDatabaseFormat(new Date()),
};

export const sendTicketsClientItemSchema = z.object({
  clientId: z.string(),
  quantity: z.number().min(0),
});

export const sendTicketsSchema = z.object({
  clients: z.array(sendTicketsClientItemSchema),
});

export type SendTicketsForm = z.infer<typeof sendTicketsSchema>;

export const addMeetingMobileFormSchema = z
  .object({
    date: z.string(),
    beginningHour: z.string(),
    endHour: z.string(),
    gender: GenderSchema,
    personalComment: z.string().optional(),
    emailOrConnectId: z
      .string()
      .min(1, "L'email ou l'ID Connect Events est requis"),
    firstName: z.string().min(1, "Le prénom est requis"),
    lastName: z.string().min(1, "Le nom est requis"),
    sendTicket: z.boolean(),
    ticketCount: z.number().min(0),
    selectedTicketId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (isBefore(new Date(data.date + " " + data.beginningHour), new Date())) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["date"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de début ne peut pas être avant maintenant.",
        path: ["beginningHour"],
      });
    }
    if (
      isBefore(
        new Date(data.date + " " + data.endHour),
        new Date(data.date + " " + data.beginningHour)
      )
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "La date de fin ne peut être avant celle du début.",
        path: ["endHour"],
      });
    }
    if (data.sendTicket && data.ticketCount === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Veuillez sélectionner au moins un billet.",
        path: ["ticketCount"],
      });
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isEmail = emailRegex.test(data.emailOrConnectId);

    if (!isEmail && data.emailOrConnectId.length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "L'ID Connect Events doit contenir au moins 3 caractères.",
        path: ["emailOrConnectId"],
      });
    }
  });

export type AddMeetingMobileFormType = z.infer<
  typeof addMeetingMobileFormSchema
>;

export const defaultAddMeetingMobileFormValues: AddMeetingMobileFormType = {
  firstName: "",
  lastName: "",
  gender: "male",
  emailOrConnectId: "",
  beginningHour: getCurrentTimeRoundedTo5Minutes(),
  endHour: getCurrentTimeRoundedTo5MinutesPlusHour(),
  date: toDatabaseFormat(new Date()),
  personalComment: "",
  sendTicket: false,
  ticketCount: 0,
  selectedTicketId: undefined,
};
